<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import {
  FORTUNE_WHEEL_PRIZE_ID,
  FORTUNE_WHEEL_PRIZE_SUBTYPE,
  FORTUNE_WHEEL_PRIZE_TYPE,
  type FortuneWheelPrize,
} from '@/types/shared/fortune-wheel'
import ChestJson from '@/assets/lotties/chest.json'
import { Vue3Lottie } from 'vue3-lottie'
import { T, useTranslate } from '@tolgee/vue'
import DoubleProfit3hImage from '@/assets/images/fortune-wheel-cards/double-profit-3h.png'
import OfflineProfit30mImage from '@/assets/images/fortune-wheel-cards/offline-profit-30m.png'
import OfflineProfit1hImage from '@/assets/images/fortune-wheel-cards/offline-profit-1h.png'
import OfflineProfit2hImage from '@/assets/images/fortune-wheel-cards/offline-profit-2h.png'
import Spins3Image from '@/assets/images/fortune-wheel-cards/spins-3.png'
import Spins5Image from '@/assets/images/fortune-wheel-cards/spins-5.png'
import Spins10Image from '@/assets/images/fortune-wheel-cards/spins-10.png'
import GoldenCoinImage from '@/assets/images/fortune-wheel-cards/golden-coin.png'
import TicketImage from '@/assets/images/gifs/ticket.gif'
import FeatherImage from '@/assets/images/feather-large.png'

import StrokeCoinImage from '@/assets/images/stroke-coin.png'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { useConfig } from '@/composables/game/config'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import BaseButton from '@/components/base/BaseButton.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { twMerge } from 'tailwind-merge'

const props = defineProps<{
  show: boolean
  prize: FortuneWheelPrize
}>()

const emit = defineEmits<{
  (e: 'ctaClick'): void
  (e: 'onCheckEntries'): void
}>()

const { topStyle } = useFullScreenDimensions()
const { t } = useTranslate()
const { config } = useConfig()
const isClickable = ref(false)
const showChest = ref(false)
const showImage = ref(false)
const showText = ref(false)

const cardImagesMap = {
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_DOUBLE_PROFIT_0_5]: DoubleProfit3hImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_OFFLINE_PROFIT_0_5]: OfflineProfit30mImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_OFFLINE_PROFIT_1]: OfflineProfit1hImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_OFFLINE_PROFIT_2]: OfflineProfit2hImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_SPIN_3]: Spins3Image,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_SPIN_5]: Spins5Image,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_SPIN_10]: Spins10Image,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_GOLDEN_COIN_1]: GoldenCoinImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_GOLDEN_COIN_3]: GoldenCoinImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_TICKET_1]: TicketImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_GOLD_FEATHER_50]: FeatherImage,
  [FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_GOLD_FEATHER_100]: FeatherImage,
}

const prizeLabel = computed(() => {
  if (
    props.prize.type !== FORTUNE_WHEEL_PRIZE_TYPE.PRIZE_BOX_ITEM ||
    (props.prize.subtype !== undefined && [FORTUNE_WHEEL_PRIZE_SUBTYPE.DOUBLE_PROFIT, FORTUNE_WHEEL_PRIZE_SUBTYPE.OFFLINE_PROFIT].includes(props.prize.subtype))
  )
    return ''
  if (props.prize.id === FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_DOUBLE_PROFIT_0_5) {
    return t.value('fortune_wheel.chest_prize.double_profit_minutes_label', 'Trading per hour doubled for {minutes} minutes!', { minutes: props.prize.amount * 60 })
  }
  if (props.prize.id === FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_OFFLINE_PROFIT_0_5) {
    return t.value('fortune_wheel.chest_prize.double_profit_minutes_label', 'Extra {minutes} minutes on top of your 3-hour profits!', { minutes: props.prize.amount * 60 })
  }
  if (props.prize.id === FORTUNE_WHEEL_PRIZE_ID.PRIZE_BOX_DOUBLE_PROFIT_3) {
    return t.value('fortune_wheel.chest_prize.double_profit_hours_label', 'Trading per hour doubled for {count, plural, one {# hour} other {# hours}}!', { count: props.prize.amount })
  }
  return t.value('fortune_wheel.chest_prize.offline_profit_hours_label', 'Extra {count, plural, one {# hour} other {# hours}} on top of your 3-hour profits!', { count: props.prize.amount })
})

const currentImage = computed(() => {
  if (!(props.prize.id in cardImagesMap)) return undefined
  return cardImagesMap[props.prize.id as keyof typeof cardImagesMap]
})

const coinReward = computed<null | number>(() => {
  if (props.prize.subtype !== FORTUNE_WHEEL_PRIZE_SUBTYPE.GOLDEN_COIN) {
    return null
  }
  return props.prize.amount * config.value.game.ecommerce.currencies.goldenCoin
})

const spinReward = computed<null | number>(() => {
  if (props.prize.subtype !== FORTUNE_WHEEL_PRIZE_SUBTYPE.SPIN) {
    return null
  }
  return props.prize.amount
})

const ticketReward = computed<null | number>(() => {
  if (props.prize.subtype !== FORTUNE_WHEEL_PRIZE_SUBTYPE.TICKET) {
    return null
  }
  return props.prize.amount
})

const featherReward = computed<null | number>(() => {
  if (props.prize.subtype !== FORTUNE_WHEEL_PRIZE_SUBTYPE.GOLD_FEATHER) {
    return null
  }
  return props.prize.amount
})

function onAnimationLoaded() {
  setTimeout(() => showImage.value = true, 2000)
}

function onContainerClick() {
  if (isClickable.value) {
    emit('ctaClick')
  }
}

watch(() => props.show, show => {
  if (show) {
    document.body.classList.add('!overflow-hidden')
    setTimeout(() => showChest.value = true, 300)
  } else {
    document.body.classList.remove('!overflow-hidden')
    isClickable.value = false
    showChest.value = false
    showImage.value = false
  }
}, { immediate: true })

watch(showImage, showImage => {
  if (!showImage) {
    showText.value = false
    return
  }
  setTimeout(() => {
    showText.value = true
    isClickable.value = true
  }, 1400)
})

useWatchOverlays(computed(() => props.show))

</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show && currentImage"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] flex flex-col z-[800]"
      @click="onContainerClick"
    >
      <CrossCircleIcon
        v-if="isClickable"
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div
        class="h-full w-full flex-1 flex flex-col gap-y-8 items-center justify-center relative mb-[60px] min-[380px]:mb-[80px]"
      >
        <div
          class="w-[90%] h-0 pb-[90%] absolute rounded-full bg-green-primary/[80] blur-[100px]"
        />
        <img
          class="absolute w-full h-auto"
          src="@/assets/images/golden-wheel-background.png"
        >
        <div class="z-[2] w-full flex items-center justify-center relative">
          <Transition name="fade">
            <Vue3Lottie
              v-if="showChest"
              :animation-data="ChestJson"
              :auto-play="true"
              :loop="false"
              width="100%"
              height="auto"
              class="min-w-[512px] min-w-[380px]:min-w-[532px]"
              :class="{'animate-blur': showImage}"
              @on-animation-loaded="onAnimationLoaded"
            />
          </Transition>
          <div class="absolute flex flex-col items-center top-1/2 -translate-y-1/2">
            <div
              v-if="showImage"
              class="relative w-full max-w-[368px]"
            >
              <Transition name="fade">
                <div
                  v-if="showText"
                  class="absolute -top-[58px] left-1/2 -translate-x-1/2 text-shadow text-[36px] leading-[42px] font-bold min-w-max"
                >
                  <T
                    key-name="fortune_wheel.chest_prize.title"
                    default-value="Bonus unlocked!"
                  />
                </div>
              </Transition>
              <div
                :class="twMerge('w-[200px] min-[380px]:w-[221px] h-auto max-w-full scale-in-image relative', !ticketReward && 'card-shadow min-[380px]:w-[258px]')"
              >
                <img
                  class="object-cover"
                  :class="featherReward && 'rotate-[23deg]'"
                  :src="currentImage"
                >
                <div
                  v-if="coinReward"
                  class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex gap-1.5 items-center w-full justify-center"
                >
                  <img
                    :src="StrokeCoinImage"
                    class="h-7 w-auto object-contain"
                  >
                  <span
                    class="text-[32px] leading-[25px] font-[900] text-white black-stroke disable-slashed-zero"
                  >
                    {{
                      formatNumberFullTruncate(coinReward)
                    }}
                  </span>
                </div>
              </div>

              <Transition name="fade">
                <div
                  v-if="showText"
                  class="absolute  left-1/2 -translate-x-1/2 flex flex-col items-center w-screen max-w-[368px]"
                  :class="ticketReward ? '-bottom-[160px]' : '-bottom-[128px]'"
                >
                  <div class="text-white/60 text-2xl font-medium">
                    <T
                      key-name="fortune_wheel.chest_prize.you_won"
                      default-value="You won"
                    />
                  </div>
                  <div
                    class="flex items-center text-light-white text-[28px] leading-8 font-bold -tracking-[0.56px] text-center px-4"
                  >
                    <div
                      v-if="coinReward"
                      class="flex gap-1.5 items-center"
                      v-tooltip="{content: formatUnshortenedNumber(coinReward), placement: 'bottom'}"
                    >
                      <CoinIcon class="h-7 w-auto text-white" />
                      <span
                        class="text-[28px] leading-[25px] font-[700] text-white disable-slashed-zero"
                      >
                        {{
                          formatNumberFullTruncate(coinReward)
                        }}
                      </span>
                    </div>
                    <div v-else-if="spinReward">
                      <T
                        key-name="fortune_wheel.info.more_spins.count_spin"
                        default-value="{count, plural, one {# spin} other {# spins}}"
                        :params="{count: spinReward}"
                      />
                    </div>
                    <div v-else-if="featherReward">
                      <T
                        key-name="fortune_wheel.chest_prize.feathers_reward"
                        default-value="{count, plural, one {# Gold Feather} other {# Gold Feathers}}"
                        :params="{count: featherReward}"
                      />
                    </div>
                    <div v-else-if="ticketReward">
                      <T
                        key-name="fortune_wheel.chest_prize.ticket_reward"
                        default-value="{count, plural, one {# Lucky Rush ticket} other {# Lucky Rush tickets}}"
                        :params="{count: ticketReward}"
                      />
                    </div>
                    <div
                      v-else
                    >
                      {{ prizeLabel }}
                    </div>
                  </div>
                  <div
                    v-if="ticketReward"
                    @click.stop.prevent="$emit('onCheckEntries')"
                    class="mt-8"
                  >
                    <BaseButton
                      class="min-w-[286px]"
                    >
                      <T
                        key-name="fortune_wheel.ticket_overlay.cta"
                        default-value="CHECK MY ENTRIES"
                      />
                    </BaseButton>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}

.golden-border {
  background: linear-gradient(90deg, #FFCB00 0%, #B57800 22%, #B77C07 26%, #BF881B 31%, #CB9D3C 37%, #DCB96A 44%, #F1DDA5 52%, #FFF4C9 56%, #EACE88 62%, #D8AD4E 68%, #CB9423 73%, #C38509 77%, #C08000 79%, #C58500 81%, #EAB200 94%, #F9C300 100%);
}

.scale-in-image {
  animation: scale-in-image 1.4s ease-out;
}

.card-shadow {
  filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.7));
}

.animate-blur {
  animation: blur-image 1s ease-out forwards;
  animation-delay: 0.4s;
}

@keyframes scale-in-image {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  20% {
    opacity: 1; /* Ensure it starts becoming visible */
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes blur-image {
  0% {
    filter: blur(0);
  }
  100% {
    filter: blur(5px);
  }
}

.text-shadow {
  text-shadow: 0px 0px 24px rgba(0, 0, 0, 0.44)
}

.black-stroke {
  text-shadow: -1.5px -1.5px 0 #000,
  1.5px -1.5px 0 #000,
  -1.5px 1.5px 0 #000,
  1.5px 1.5px 0 #000,
  -2px 0 0 #000,
  2px 0 0 #000,
  0 -2px 0 #000,
  0 2px 0 #000,
  -1px -1px 0 #000, /* Finer shadow adjustments */
  1px -1px 0 #000,
  -1px 1px 0 #000,
  1px 1px 0 #000;
}
</style>